import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { CompanyModel } from '@data-layer/models/companies';
import { TranslateService } from '@ngx-translate/core';
import { DESC_ENTITY } from '@constants/desc-entity';
import { isImporterFromCompany } from '@helpers/importer.helper';
import { IsfHelper, IsfSameAsParent } from '@helpers/isf.helper';

@Component({
  selector: 'desktop-isf-summary-receiving-party',
  template: `
    <desktop-isf-summary-card [header]="'LABELS.RECEIVING_PARTY' | translate">
      <ng-container body>
        <!--#12041-->
        <desktop-summary-entity
          [entity]="importer"
          [isShowIdentifier]="true"
          [entityCode]="enumEntityModelCode.IMPORTER"
          [isEditedRecently]="isImporterEdited"
          [isEditMode]="false"
          [sameAs]="importerSameAsCompany()"
          (edit)="edit.emit({ id: importer?.id, entityType: enumEntityModelCode.IMPORTER })"
        ></desktop-summary-entity>

        <desktop-summary-entity
          [entity]="consignee"
          [isShowIdentifier]="true"
          [entityCode]="enumEntityModelCode.CONSIGNEE"
          [isEditedRecently]="isConsigneeEdited"
          [isEditMode]="isEditMode"
          [isShowEncircledButton]="true"
          [sameAs]="isSameAs(consignee, enumEntityModelCode.CONSIGNEE)"
          (edit)="edit.emit({ id: consignee?.id, entityType: enumEntityModelCode.CONSIGNEE })"
        ></desktop-summary-entity>

        <desktop-summary-entity
          [entity]="buyer"
          [isShowIdentifier]="true"
          [entityCode]="enumEntityModelCode.BUYER"
          [isEditedRecently]="isBuyerEdited"
          [isEditMode]="isEditMode"
          [isShowEncircledButton]="true"
          [sameAs]="isSameAs(buyer, enumEntityModelCode.BUYER)"
          (edit)="edit.emit({ id: buyer?.id, entityType: enumEntityModelCode.BUYER })"
        ></desktop-summary-entity>

        <desktop-summary-entity
          [entity]="shipTo"
          [isShowIdentifier]="false"
          [entityCode]="enumEntityModelCode.SHIP_TO"
          [isEditedRecently]="isShipToEdited"
          [isEditMode]="isEditMode"
          [isShowEncircledButton]="true"
          [isLastInRow]="true"
          [sameAs]="isSameAs(shipTo, enumEntityModelCode.SHIP_TO)"
          (edit)="edit.emit({ id: shipTo?.id, entityType: enumEntityModelCode.SHIP_TO })"
        ></desktop-summary-entity>
      </ng-container>
    </desktop-isf-summary-card>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSummaryReceivingPartyComponent {
  @Input() public importer: EntityModel;
  @Input() public consignee: EntityModel;
  @Input() public buyer: EntityModel;
  @Input() public shipTo: EntityModel;
  @Input() public isImporterEdited: boolean;
  @Input() public isConsigneeEdited: boolean;
  @Input() public isBuyerEdited: boolean;
  @Input() public isShipToEdited: boolean;
  @Input() public company: CompanyModel;
  @Input() public isEditMode: boolean;
  @Output() public edit: EventEmitter<{ id: number; entityType: enumEntityModelCode }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
  }>();

  public enumEntityModelCode = enumEntityModelCode;
  public enumEntityByKey = enumEntityByKey;

  constructor(private translateService: TranslateService) {}

  public importerSameAsCompany(): string {
    if (isImporterFromCompany(this.importer, this.company)) {
      const entityName: string = this.translateService.instant('LABELS.IOR_PROFILE');
      return this.translateService.instant('LABELS.SAME_AS', { entityName });
    }
  }

  // #13536: "Same as <party>" is decided by identity with the preceding party (same entity row), not by entityCode
  public isSameAs(entity: EntityModel, entityCode: enumEntityModelCode): string {
    const parentCode: enumEntityModelCode = IsfHelper.getSameAsParentCode(entity, this.getSameAsParents(entityCode));
    if (parentCode === null) {
      return;
    }

    const entityName: string = this.translateService.instant(DESC_ENTITY[parentCode].titleDesktop);
    return this.translateService.instant('LABELS.SAME_AS', { entityName });
  }

  private getSameAsParents(entityCode: enumEntityModelCode): IsfSameAsParent[] {
    const parents: IsfSameAsParent[] = [
      { code: enumEntityModelCode.IMPORTER, entity: this.importer },
      { code: enumEntityModelCode.CONSIGNEE, entity: this.consignee },
      { code: enumEntityModelCode.BUYER, entity: this.buyer },
    ];
    const slotIndex: number = parents.findIndex((parent: IsfSameAsParent) => parent.code === entityCode);

    return slotIndex === -1 ? parents : parents.slice(0, slotIndex);
  }
}
